import { JobDetailsPreview } from '@seek/job-details-view';
import { useTranslations } from '@vocab/react';
import {
  Box,
  Button,
  IconPrint,
  Stack,
  Tab,
  TabPanel,
  TabPanels,
  Tabs,
  TabsProvider,
} from 'braid-design-system';
import React, { useState } from 'react';

import translations from './.vocab';
import { PreviewLayoutSelector } from './components/PreviewLayoutSelector';
import { SearchResultPreview } from './components/SearchResultPreview';
import { type PreviewLayout, PreviewLayoutEnum } from './types';

import * as styles from './index.css';

interface Props {
  id?: string;
  jobDetailsPreviewId?: string;
  jobSearchResultId?: string;
  jobDetails: any;
  jobDetailsId?: string;
  onPrintClick: (index: number) => void;
  onTabClick: (index: number) => void;
  serpDetails?: any;
  serpDetailsId?: string;
}

export const JobPreview = ({
  id = 'job-preview',
  jobDetailsPreviewId = 'job-details-preview',
  jobDetails,
  jobDetailsId = 'job-preview-job-details',
  onPrintClick,
  onTabClick,
  serpDetails,
  serpDetailsId = 'job-preview-serp-details',
}: Props) => {
  const { t } = useTranslations(translations);

  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [previewLayout, setPreviewLayout] = useState(PreviewLayoutEnum.DESKTOP);

  const handleTabClick = (index: number) => {
    setPreviewLayout(PreviewLayoutEnum.DESKTOP);
    setSelectedTabIndex(index);

    if (onTabClick) {
      onTabClick(index);
    }
  };

  const handleLayoutClick = (layout: PreviewLayout) => {
    setPreviewLayout(layout);
  };

  const handlePrintButtonClick = () => {
    const iframe = document.querySelector(
      `#${jobDetailsPreviewId} iframe`,
    ) as HTMLIFrameElement;

    if (onPrintClick) {
      onPrintClick(selectedTabIndex);
    }

    iframe.contentWindow?.print();
  };

  const tab1Label = t('job-preview-tab-1-label');
  const tab2Label = t('job-preview-tab-2-label');

  const isDesktopLayout = previewLayout === PreviewLayoutEnum.DESKTOP;
  const isJobDetailsTabSelected = selectedTabIndex === 0;

  const jobDetailsPreviewClassNames = [
    styles.jobDetailsPreview,
    isDesktopLayout
      ? styles.jobDetailsPreviewDesktop
      : styles.jobDetailsPreviewMobile,
  ];

  return (
    <TabsProvider id={id} onChange={(index) => handleTabClick(index)}>
      <Stack space="none">
        <Box position="relative">
          <Tabs label={t('job-preview-tabs-label')} align="center">
            <Tab>{tab1Label}</Tab>
            <Tab>{tab2Label}</Tab>
          </Tabs>
          {isDesktopLayout && isJobDetailsTabSelected ? (
            <Box
              bottom={0}
              right={0}
              position="absolute"
              display={['none', 'none', 'block']}
            >
              <Button variant="transparent" onClick={handlePrintButtonClick}>
                <IconPrint />
                {` ${t('job-preview-button-label')} ${tab1Label}`}
              </Button>
            </Box>
          ) : null}
        </Box>
        <Box padding="large" background="body">
          <Stack space="large">
            <PreviewLayoutSelector
              layout={previewLayout}
              onLayoutClick={handleLayoutClick}
            />
            <TabPanels>
              <TabPanel>
                <Box className={jobDetailsPreviewClassNames}>
                  <Box
                    id="job-details-preview"
                    className={
                      isDesktopLayout
                        ? styles.desktopContainer
                        : styles.mobileContainer
                    }
                  >
                    <JobDetailsPreview
                      job={{ ...jobDetails, applyUrl: 'about:blank' }}
                      uniqueContainerValue={jobDetailsId}
                      ctas={{
                        onApplyClick: () => {},
                        onToggleSaveJob: () => {},
                        onReportJobAd: () => Promise.resolve(true),
                        trackVerifiedHirerBadgeClick: () => {},
                      }}
                      featureFlags={{
                        stickApplyButtonOnDesktop: !isDesktopLayout && true,
                      }}
                      layout={isDesktopLayout ? 'desktop' : 'mobile'}
                    />
                  </Box>
                </Box>
              </TabPanel>
              <TabPanel>
                <Box
                  id="search-results-preview"
                  className={styles.searchResultPreview}
                  maxWidth={isDesktopLayout ? 'large' : 'xsmall'}
                >
                  <SearchResultPreview
                    id={serpDetailsId}
                    serpDetails={serpDetails}
                  />
                </Box>
              </TabPanel>
            </TabPanels>
          </Stack>
        </Box>
      </Stack>
    </TabsProvider>
  );
};
